import { jsPDF } from "jspdf";

const sanitize = (str) => {
  if (str == null) return "";
  return String(str).replace(/\r?\n/g, " ").trim();
};

const drawWatermark = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const cx = pageWidth / 2;
  const cy = pageHeight / 2;
  doc.setFontSize(60);
  doc.setTextColor(200, 242, 250);
  doc.setFont("helvetica", "normal");
  doc.text("DIGIXLEADS", cx + 15, cy, { align: "center", baseline: "middle", angle: 45 });
};

/**
 * Generate quote PDF from payload.
 * @param {Object} data
 * @param {Object} data.customerInfo
 * @param {Object} data.quoteMeta
 * @param {Array} data.lineItems
 * @param {Array} data.availableServices
 * @param {string} data.packageTotal
 * @param {string} data.terms
 * @param {boolean} data.leadGenerationCampaign
 * @param {Object} data.leadGenerationTargets
 * @param {Object} data.leadGenerationGoals
 * @param {string|null} data.checkmarkImageDataUrl
 */
export function generateQuotePdf(data) {
  const {
    customerInfo = {},
    quoteMeta = {},
    lineItems = [],
    availableServices = [],
    packageTotal = "",
    terms = "",
    leadGenerationCampaign = false,
    leadGenerationTargets = {},
    leadGenerationGoals = {},
    checkmarkImageDataUrl = null
  } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPos = 20;

  drawWatermark(doc);

  const defaultTextStyle = () => {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
  };
  const headingStyle = () => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
  };
  defaultTextStyle();

  const drawGreenCheckmark = (x, y) => {
    if (checkmarkImageDataUrl) {
      const w = 4;
      const h = 4;
      doc.addImage(checkmarkImageDataUrl, "PNG", x, y - 1, w, h);
      return;
    }
    doc.setDrawColor(34, 197, 94);
    doc.setLineWidth(0.9);
    doc.line(x, y + 3, x + 2, y + 1);
    doc.line(x + 2, y + 1, x + 5, y + 5);
  };

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.text("QUOTE", 20, yPos);
  defaultTextStyle();
  const quoteDateDisplay = quoteMeta.quoteDate || new Date().toISOString().split("T")[0];
  doc.text(`Date: ${quoteDateDisplay}`, pageWidth - 20, yPos, { align: "right" });
  yPos += 8;
  doc.setLineWidth(0.5);
  doc.setDrawColor(41, 119, 237);
  doc.line(20, yPos, pageWidth - 20, yPos);
  yPos += 10;

  /* DigixLeads left, customer details right (receipt-style header block) */
  let leftY = yPos;
  let rightY = yPos - 2;

  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  const digiPart = "Digi";
  const xleadsPart = "XLeads";
  doc.setTextColor(0, 163, 218);
  doc.text(digiPart, 20, leftY);
  doc.setTextColor(58, 76, 93);
  doc.text(xleadsPart, 20 + doc.getTextWidth(digiPart), leftY);
  leftY += 9;

  defaultTextStyle();
  ["Hyderabad, Telangana, 500079", "+91 (814) 225 - 1234", "info@tumma.co"].forEach((line) => {
    doc.text(line, 20, leftY);
    leftY += 5;
  });

  const customerName = sanitize(customerInfo.contactPerson);
  if (customerName) {
    doc.text(customerName, pageWidth - 20, rightY, { align: "right" });
    rightY += 5;
  }
  const companyName = sanitize(customerInfo.companyName);
  if (companyName) {
    const companyLines = doc.splitTextToSize(companyName, pageWidth - 60);
    doc.text(companyLines, pageWidth - 20, rightY, { align: "right" });
    rightY += companyLines.length * 5;
  }
  const address = sanitize(customerInfo.address);
  if (address) {
    const addressLines = doc.splitTextToSize(address, pageWidth - 60);
    doc.text(addressLines, pageWidth - 20, rightY, { align: "right" });
    rightY += addressLines.length * 5;
  }
  const phone = sanitize(customerInfo.phone);
  if (phone) {
    doc.text(phone, pageWidth - 20, rightY, { align: "right" });
    rightY += 5;
  }
  yPos = Math.max(leftY, rightY) + 6;

  const tableMargin = 20;
  const tableRight = pageWidth - tableMargin;
  const colServicesX = tableMargin + 8;
  const colPackageX = tableRight - 8;
  const headerWidth = pageWidth - tableMargin * 2;
  const leftWidth = headerWidth * 0.75;
  const rightWidth = headerWidth * 0.25;
  const rowPadding = 2;
  const headerHeight = 7;

  doc.setFillColor(41, 119, 237);
  doc.rect(tableMargin, yPos, leftWidth, headerHeight, "F");
  doc.setFillColor(30, 90, 200);
  doc.rect(tableMargin + leftWidth, yPos, rightWidth, headerHeight, "F");
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  const typeOfServicesLabel = sanitize(customerInfo.typeOfServices);
  const servicesHeaderText = typeOfServicesLabel ? `${typeOfServicesLabel} Services` : "Services";
  doc.text(servicesHeaderText, colServicesX, yPos + headerHeight / 2 + 1.5);
  doc.text("Package", colPackageX, yPos + headerHeight / 2 + 1.5, { align: "right" });
  yPos += headerHeight + 4;

  defaultTextStyle();
  const servicesColumnWidth = leftWidth - 16;
  const packageColumnWidth = rightWidth - 16;

  if (lineItems.length === 0) {
    doc.text("No services selected", colServicesX, yPos + rowPadding);
    yPos += 14;
  } else {
    lineItems.forEach((item, idx) => {
      if (yPos > pageHeight - 35) {
        doc.addPage();
        yPos = 20;
        drawWatermark(doc);
      }
      const defaultName = availableServices.find((s) => String(s.id) === String(item.serviceId))?.name;
      const serviceName = item.customName !== undefined && item.customName !== "" ? item.customName : defaultName || "Custom Package";
      const safeName = sanitize(serviceName).replace(/\s+/g, " ");
      const desc = sanitize(item.description || "").trim();
      const freeText = sanitize(item.freeText || "").trim();
      const packageText = freeText !== "" ? freeText : (desc !== "" && desc !== "✓" ? desc : "");
      const showTick = packageText === "";

      const serviceLines = doc.splitTextToSize(safeName, servicesColumnWidth);
      const leftHeight = Array.isArray(serviceLines) ? serviceLines.length * 5 : 5;
      let rightHeight = 5;
      if (packageText !== "") {
        const pl = doc.splitTextToSize(packageText, packageColumnWidth);
        rightHeight = Array.isArray(pl) ? pl.length * 5 : 5;
      }
      const rowHeight = Math.max(leftHeight, rightHeight) + rowPadding * 2;

      defaultTextStyle();
      if (Array.isArray(serviceLines)) {
        doc.text(serviceLines, colServicesX, yPos + rowPadding + 2);
      } else {
        doc.text(serviceLines, colServicesX, yPos + rowPadding + 2);
      }
      const rowCenterY = yPos + rowPadding + 2;
      if (showTick) {
        drawGreenCheckmark(colPackageX - 4, rowCenterY - 2);
      } else {
        const packageLinesArr = doc.splitTextToSize(packageText, packageColumnWidth);
        if (Array.isArray(packageLinesArr)) {
          doc.text(packageLinesArr, colPackageX, rowCenterY, { align: "right" });
        } else {
          doc.text(packageText, colPackageX, rowCenterY, { align: "right" });
        }
      }

      yPos += rowHeight;

      if (idx < lineItems.length - 1) {
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.line(tableMargin, yPos, tableRight, yPos);
        yPos += 2;
      }
    });
  }

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 5;

  defaultTextStyle();
  doc.setFont("helvetica", "bold");
  doc.text("Package Total", colServicesX, yPos);
  defaultTextStyle();
  const packageAmountRaw = sanitize(packageTotal) || "";
  const packageAmountText = packageAmountRaw
    ? packageAmountRaw.replace(/₹/g, "Rs.").replace(/\s+/g, " ").trim()
    : "";
  doc.text(packageAmountText, colPackageX, yPos, { align: "right" });
  yPos += 5;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 5;

  if (leadGenerationCampaign) {
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc);
      defaultTextStyle();
    }
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Lead Generation Campaign", 20, yPos);
    yPos += 10;
    defaultTextStyle();
    doc.setFontSize(10);
    const targets = [
      `Location: ${sanitize(leadGenerationTargets.location) || "—"}`,
      `Age: ${sanitize(leadGenerationTargets.age) || "—"}`,
      `Gender: ${sanitize(leadGenerationTargets.gender) || "—"}`
    ];
    targets.forEach((t) => {
      doc.text(t, 20, yPos);
      yPos += 6;
    });
    yPos += 4;
    const goals = [
      `Ad Budget (Meta Ads): Rs. ${sanitize(leadGenerationGoals.metaAdsBudget) || "0"}`,
      `Reach: ${sanitize(leadGenerationGoals.reach) || "—"}`,
      `Expected Leads: ${sanitize(leadGenerationGoals.expectedLeads) || "—"}`,
      `Ad Budget (Google Ads): Rs. ${sanitize(leadGenerationGoals.googleAdsBudget) || "0"}`
    ];
    goals.forEach((g) => {
      doc.text(g, 20, yPos);
      yPos += 6;
    });
    yPos += 8;
    defaultTextStyle();
    const noteLabel = "Note: ";
    doc.setFont("helvetica", "bold");
    doc.text(noteLabel, 20, yPos);
    const noteLabelWidth = doc.getTextWidth(noteLabel);
    doc.setFont("helvetica", "normal");
    doc.text("Ads Budget mentioned above is separate from Package Total.", 20 + noteLabelWidth, yPos);
    yPos += 8;
  }

  if (terms) {
    yPos += 10;
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc);
      defaultTextStyle();
    }
    headingStyle();
    doc.text("Terms & Conditions:", 20, yPos);
    yPos += 8;
    defaultTextStyle();
    const sourceLines = String(terms || "").split(/\r?\n/).map((line) => line.trim());
    for (const line of sourceLines) {
      if (!line) {
        yPos += 6;
        continue;
      }
      const trimmed = line;
      const isHeading = trimmed.endsWith(":") && !trimmed.startsWith("•");
      if (isHeading) {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
      }
      const wrapped = doc.splitTextToSize(line || " ", pageWidth - 40);
      const lineHeight = wrapped.length * 6;
      if (yPos + lineHeight > pageHeight - 25) {
        doc.addPage();
        yPos = 20;
        drawWatermark(doc);
        defaultTextStyle();
      }
      doc.text(wrapped, 20, yPos);
      yPos += lineHeight;
      if (isHeading) {
        defaultTextStyle();
      }
    }
    yPos += 10;
    defaultTextStyle();
    doc.setFont("helvetica", "bold");
    doc.text("We truly appreciate your business", pageWidth / 2, yPos, { align: "center" });
    yPos += 10;
  }

  return doc;
}
